import React, { useContext, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../../providers/AuthContext';
import { getAdminAuthHeaders } from '../../utils/adminApi';

const MAX_FILES = 20;

const ImageUpload = ({ onUpload, folder = 'products' }) => {
  const { token } = useContext(AuthContext);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const addFilesToQueue = (newFiles) => {
    const available = MAX_FILES - files.length;
    if (available <= 0) return;
    const toAdd = newFiles.slice(0, available);
    setFiles(prev => [...prev, ...toAdd]);
    const urls = toAdd.map(f => URL.createObjectURL(f));
    setPreviews(prev => [...prev, ...urls]);
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length === 0) return;
    addFilesToQueue(selected);
    e.target.value = '';
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (dropped.length > 0) addFilesToQueue(dropped);
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setProgress(0);

    try {
      // 1. Get presigned PUT URLs (bypasses Vercel 4.5 MB limit)
      const presignPayload = files.map((file, i) => ({
        originalname: file.name,
        contentType: file.type || 'image/jpeg',
        folder,
        index: i,
      }));
      const presignRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/media/presign`,
        { files: presignPayload },
        { headers: getAdminAuthHeaders(token) }
      );
      const presignedFiles = presignRes.data;

      // 2. Upload each file directly to Wasabi
      const uploadedFiles = [];
      for (let i = 0; i < presignedFiles.length; i++) {
        const { presignedUrl, key, url, originalIndex } = presignedFiles[i];
        const file = files[originalIndex];
        await fetch(presignedUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'image/jpeg' },
        });
        uploadedFiles.push({
          key, url,
          originalname: file.name,
          folder,
          tags: '',
          description: '',
          size: file.size,
          mimetype: file.type || 'image/jpeg',
        });
        setProgress(Math.round(((i + 1) / presignedFiles.length) * 100));
      }

      // 3. Register in MongoDB
      const registerRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/media/register`,
        { files: uploadedFiles },
        { headers: getAdminAuthHeaders(token) }
      );
      const saved = registerRes.data;
      if (onUpload) {
        (Array.isArray(saved) ? saved : [saved]).forEach(item => onUpload(item));
      }
      setFiles([]);
      setPreviews([]);
    } catch (error) {
      console.error('Error al subir la imagen', error);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const removePreview = (index) => {
    URL.revokeObjectURL(previews[index]);
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="imageupload-bulk">
      <div className="imageupload-bulk-input">
        <label
          className={`imageupload-bulk-label${isDragging ? ' drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <i className={`fas ${isDragging ? 'fa-cloud-arrow-down' : 'fa-cloud-arrow-up'}`}></i>
          <span>
            {isDragging
              ? 'Suelta las imágenes aquí...'
              : files.length > 0
                ? `${files.length}/${MAX_FILES} archivo(s) seleccionado(s)`
                : 'Arrastra o selecciona imágenes (máx. 20)'}
          </span>
          <input type="file" onChange={handleFileChange} accept="image/*" multiple style={{ display: 'none' }} />
        </label>
        <button
          className="imageupload-bulk-btn"
          onClick={handleUpload}
          disabled={files.length === 0 || uploading}
        >
          {uploading ? (
            <><i className="fas fa-spinner fa-spin"></i> Subiendo... {progress > 0 ? `${progress}%` : ''}</>
          ) : (
            <><i className="fas fa-upload"></i> Subir {files.length > 1 ? `(${files.length})` : 'Imagen'}</>
          )}
        </button>
      </div>
      {previews.length > 0 && (
        <div className="imageupload-bulk-previews">
          {previews.map((url, index) => (
            <div key={index} className="imageupload-bulk-thumb">
              <img src={url} alt={`Preview ${index + 1}`} />
              <button type="button" className="imageupload-bulk-remove" onClick={() => removePreview(index)}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
